import { IndicationUnit } from "@solomon-apps/shared/src/components/Metrics/IndicationUnit";
import { Button } from "@solomon-apps/shared/src/components/ui/button";
import { Card } from "@solomon-apps/shared/src/components/ui/card";
import { Text } from "@solomon-apps/shared/src/components/ui/text";
import { cn } from "@solomon-apps/shared/src/utils/cn";
import React, { useState } from "react";

import {
    CurrencyCappedInput,
    CurrencyDelegatedInput,
    DaysSliderInput,
    PercentageSplitInput,
    RangeAxisInput,
    TierSegmentedInput
} from "./PolicyValueEditor";

// ---------------------------------------------------------------------------
// Shared shell — mirrors the real PolicyEditor card so the exploration
// page comparison stays apples-to-apples.
// ---------------------------------------------------------------------------

function ProposedShell({
    helper,
    children,
    diffFrom,
    diffTo,
    diffUnit
}: {
    helper: string;
    children: React.ReactNode;
    diffFrom?: string;
    diffTo?: string;
    diffUnit?: string;
}): React.ReactElement {
    return (
        <Card surface="sunken" rounded={false} className="p-6">
            <div className="mb-5 flex flex-col gap-1 md:flex-row md:items-center md:justify-between">
                <Text variant="label" className="text-foreground-muted">
                    Set value
                </Text>
                <Text variant="fine-print" className="text-foreground-muted">
                    {helper}
                </Text>
            </div>
            {children}
            <div className="mt-6 flex flex-wrap items-center gap-3">
                <Button>Save value</Button>
                {diffFrom && diffTo && diffFrom !== diffTo ? (
                    <Text
                        variant="fine-print"
                        render={<span />}
                        className="text-foreground-neutral"
                    >
                        <span className="text-foreground-muted">
                            {diffFrom}
                        </span>
                        <span className="mx-1.5">→</span>
                        <span className="text-foreground-base">{diffTo}</span>
                        {diffUnit ? (
                            <span className="ml-1 text-foreground-muted">
                                {diffUnit}
                            </span>
                        ) : null}
                    </Text>
                ) : null}
            </div>
        </Card>
    );
}

// ---------------------------------------------------------------------------
// Exploration wrappers — each one owns local state so the exploration
// page can interact with them in isolation. The real inputs come from
// PolicyValueEditor.tsx.
// ---------------------------------------------------------------------------

export function TierSegmentedProposal(): React.ReactElement {
    const [draft, setDraft] = useState("2");
    return (
        <ProposedShell
            helper="Distributor allows tier 1–3"
            diffFrom="Tier 2"
            diffTo={`Tier ${draft}`}
        >
            <TierSegmentedInput draft={draft} onDraftChange={setDraft} />
        </ProposedShell>
    );
}

export function DaysSliderProposal(): React.ReactElement {
    const [draft, setDraft] = useState("30");
    const cap = 90;
    return (
        <ProposedShell
            helper={`Distributor allows up to ${cap} days`}
            diffFrom="30 days"
            diffTo={`${draft} days`}
        >
            <DaysSliderInput draft={draft} onDraftChange={setDraft} cap={cap} />
        </ProposedShell>
    );
}

export function PercentageSplitProposal(): React.ReactElement {
    const [draft, setDraft] = useState("65");
    return (
        <ProposedShell
            helper="No distributor limit on this rule"
            diffFrom="65%"
            diffTo={`${draft}%`}
        >
            <PercentageSplitInput draft={draft} onDraftChange={setDraft} />
        </ProposedShell>
    );
}

export function CurrencyCappedProposal(): React.ReactElement {
    const [draft, setDraft] = useState("2500");
    const cap = 5000;
    return (
        <ProposedShell
            helper={`Distributor allows up to ${cap.toLocaleString()}`}
            diffFrom="2,500"
            diffTo={Number(draft).toLocaleString()}
        >
            <CurrencyCappedInput
                draft={draft}
                onDraftChange={setDraft}
                cap={cap}
                unit=""
            />
        </ProposedShell>
    );
}

export function CurrencyDelegatedProposal(): React.ReactElement {
    const [draft, setDraft] = useState("10000");
    return (
        <ProposedShell
            helper="No distributor limit on this rule"
            diffFrom="1,001"
            diffTo={Number(draft).toLocaleString()}
        >
            <CurrencyDelegatedInput
                draft={draft}
                onDraftChange={setDraft}
                unit=""
            />
        </ProposedShell>
    );
}

export function RangeAxisProposal(): React.ReactElement {
    const [draft, setDraft] = useState("5000");
    const floor = 0;
    const ceiling = 10000;
    return (
        <ProposedShell
            helper={`Distributor allows ${floor}–${ceiling.toLocaleString()}`}
            diffFrom="5,000"
            diffTo={Number(draft).toLocaleString()}
        >
            <RangeAxisInput
                draft={draft}
                onDraftChange={setDraft}
                floor={floor}
                ceiling={ceiling}
                unit=""
            />
        </ProposedShell>
    );
}

// ---------------------------------------------------------------------------
// State demos — saving / validation / save error
// ---------------------------------------------------------------------------

export function SavingStateProposal(): React.ReactElement {
    return (
        <Card surface="sunken" rounded={false} className="p-6">
            <div className="mb-5 flex flex-col gap-1 md:flex-row md:items-center md:justify-between">
                <Text variant="label" className="text-foreground-muted">
                    Set value
                </Text>
                <Text variant="fine-print" className="text-foreground-muted">
                    No distributor limit on this rule
                </Text>
            </div>
            <div className="h-14 opacity-60 pointer-events-none border border-line-base bg-surface-sunken flex items-center px-3 gap-3">
                <Text
                    variant="number"
                    render={<span />}
                    className="text-2xl text-foreground-base flex-1"
                >
                    1001
                </Text>
                <span className="inline-flex items-center gap-2">
                    <span className="inline-block size-3 animate-spin rounded-full border border-foreground-neutral border-t-transparent" />
                    <Text
                        variant="fine-print"
                        render={<span />}
                        className="text-foreground-muted"
                    >
                        Committing
                    </Text>
                </span>
            </div>
            <div className="mt-6">
                <Button disabled>Saving...</Button>
            </div>
        </Card>
    );
}

export function ValidationErrorProposal(): React.ReactElement {
    const [draft, setDraft] = useState("999999999");
    const cap = 10000;
    const invalid = Number(draft) > cap;
    return (
        <ProposedShell
            helper={`Distributor allows up to ${cap.toLocaleString()}`}
        >
            <div className="space-y-2">
                <div
                    className={cn(
                        "border border-line-base bg-surface-sunken flex items-center h-14",
                        invalid && "border-error-base"
                    )}
                >
                    <input
                        value={draft}
                        inputMode="numeric"
                        onChange={(event) =>
                            setDraft(event.target.value.replace(/\D/g, ""))
                        }
                        aria-invalid={invalid || undefined}
                        className="flex-1 h-full bg-transparent text-2xl text-foreground-base focus:outline-none px-2"
                    />
                </div>
                {invalid ? (
                    <Text variant="fine-print" className="text-error-base">
                        Exceeds distributor cap of {cap.toLocaleString()}.
                        Reduce to continue, or request a cap increase.
                    </Text>
                ) : null}
            </div>
        </ProposedShell>
    );
}

export function SaveErrorProposal(): React.ReactElement {
    return (
        <div className="space-y-3">
            <div className="border border-error-base/40 bg-error-base/5 px-4 py-3 flex items-start justify-between gap-4">
                <div className="flex items-start gap-3">
                    <div className="mt-1 shrink-0">
                        <IndicationUnit
                            variant="status"
                            state="error"
                            size={6}
                        />
                    </div>
                    <div>
                        <Text variant="label" className="text-error-base">
                            Couldn&apos;t save
                        </Text>
                        <Text
                            variant="fine-print"
                            className="mt-1 text-foreground-muted"
                        >
                            Program policy scope unavailable. Your draft is
                            preserved.
                        </Text>
                    </div>
                </div>
                <Button variant="sunken" outline>
                    Retry
                </Button>
            </div>
            <Card surface="sunken" rounded={false} className="p-6">
                <div className="mb-5 flex flex-col gap-1 md:flex-row md:items-center md:justify-between">
                    <Text variant="label" className="text-foreground-muted">
                        Set value
                    </Text>
                    <Text
                        variant="fine-print"
                        className="text-foreground-muted"
                    >
                        No distributor limit on this rule
                    </Text>
                </div>
                <div className="h-14 border border-line-base bg-surface-sunken flex items-center px-3 gap-3">
                    <Text
                        variant="number"
                        render={<span />}
                        className="text-2xl text-foreground-base"
                    >
                        5000
                    </Text>
                </div>
                <div className="mt-6 flex gap-3">
                    <Button>Save value</Button>
                </div>
            </Card>
        </div>
    );
}
